// HILAND Survey — UI primitives
// One place for every surface, control and state used across the admin app.
// Rules of the system:
//   · 8px spacing rhythm, rounded-xl surfaces, rounded-lg controls
//   · every interactive element is >= 40px tall on touch, has a visible focus ring
//   · colour carries meaning (brand = action, emerald = good, amber = attention, red = bad)
//   · nothing is desktop-only: tables collapse, modals become sheets, bars scroll
import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  Search, X, ChevronLeft, ChevronRight, ChevronDown, Info,
  TriangleAlert, CircleAlert, CircleCheck, LoaderCircle, Inbox, ArrowUpDown, ArrowUp, ArrowDown,
} from "lucide-react";

/* ═══════════════════════════════════════════════════════════════
   Tokens
   ═══════════════════════════════════════════════════════════════ */

export const cx = (...parts) => parts.filter(Boolean).join(" ");

const RING = "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40 focus-visible:ring-offset-1 focus-visible:ring-offset-white";

/* ═══════════════════════════════════════════════════════════════
   Badges & status
   ═══════════════════════════════════════════════════════════════ */

const BADGE_VARIANTS = {
  default: "bg-ink-100 text-ink-600 ring-ink-200",
  neutral: "bg-ink-100 text-ink-600 ring-ink-200",
  brand:   "bg-brand-50 text-brand-700 ring-brand-200",
  success: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  warning: "bg-amber-50 text-amber-700 ring-amber-200",
  danger:  "bg-red-50 text-red-700 ring-red-200",
  info:    "bg-blue-50 text-blue-700 ring-blue-200",
  purple:  "bg-violet-50 text-violet-700 ring-violet-200",
  orange:  "bg-orange-50 text-orange-700 ring-orange-200",
  cyan:    "bg-cyan-50 text-cyan-700 ring-cyan-200",
  pink:    "bg-pink-50 text-pink-700 ring-pink-200",
};

const DOT_COLORS = {
  default: "bg-ink-400", neutral: "bg-ink-400", brand: "bg-brand-500",
  success: "bg-emerald-500", warning: "bg-amber-500", danger: "bg-red-500",
  info: "bg-blue-500", purple: "bg-violet-500", orange: "bg-orange-500",
  cyan: "bg-cyan-500", pink: "bg-pink-500",
};

export const Badge = ({ children, variant = "default", size = "sm", dot = false, className = "", title }) => {
  const s = { xs: "text-[10px] px-1.5 py-0.5 gap-1", sm: "text-xs px-2 py-0.5 gap-1.5", md: "text-sm px-2.5 py-1 gap-1.5" };
  return (
    <span title={title}
      className={cx("inline-flex items-center rounded-full font-medium ring-1 ring-inset whitespace-nowrap",
        BADGE_VARIANTS[variant] || BADGE_VARIANTS.default, s[size] || s.sm, className)}>
      {dot && <span className={cx("w-1.5 h-1.5 rounded-full shrink-0", DOT_COLORS[variant] || DOT_COLORS.default)} />}
      {children}
    </span>
  );
};

// Every status string the API can return, mapped to a label + colour.
const STATUS_MAP = {
  // sites
  active: { label: "Active", variant: "success" },
  inactive: { label: "Inactive", variant: "default" },
  completed: { label: "Completed", variant: "info" },
  "on-hold": { label: "On Hold", variant: "warning" },
  // resources
  available: { label: "Available", variant: "success" },
  assigned: { label: "Assigned", variant: "purple" },
  maintenance: { label: "Maintenance", variant: "warning" },
  // approvals
  pending: { label: "Pending", variant: "warning" },
  approved: { label: "Approved", variant: "success" },
  rejected: { label: "Rejected", variant: "danger" },
  // bills
  draft: { label: "Draft", variant: "default" },
  sent: { label: "Sent", variant: "info" },
  paid: { label: "Paid", variant: "success" },
  overdue: { label: "Overdue", variant: "danger" },
  cancelled: { label: "Cancelled", variant: "default" },
  // attendance
  present: { label: "Present", variant: "success" },
  absent: { label: "Absent", variant: "danger" },
  "half-day": { label: "Half Day", variant: "warning" },
  leave: { label: "Leave", variant: "info" },
  // inquiries
  new: { label: "New", variant: "brand" },
  contacted: { label: "Contacted", variant: "info" },
  qualified: { label: "Qualified", variant: "purple" },
  proposal_sent: { label: "Proposal Sent", variant: "cyan" },
  converted: { label: "Converted", variant: "success" },
  lost: { label: "Lost", variant: "danger" },
  // priority
  low: { label: "Low", variant: "default" },
  medium: { label: "Medium", variant: "warning" },
  high: { label: "High", variant: "danger" },
};

export const StatusBadge = ({ status, size = "sm", dot = true }) => {
  if (!status) return <span className="text-ink-300">—</span>;
  const cfg = STATUS_MAP[status] || { label: String(status).replace(/_/g, " "), variant: "default" };
  return <Badge variant={cfg.variant} size={size} dot={dot} className="capitalize">{cfg.label}</Badge>;
};

// Days-to-expiry, colour coded. Stacked label + pill.
const expiryTone = (days) => {
  if (days < 0) return { variant: "danger", text: "Expired" };
  if (days === 0) return { variant: "danger", text: "Today" };
  if (days <= 7) return { variant: "danger", text: `${days}d left` };
  if (days <= 30) return { variant: "orange", text: `${days}d left` };
  if (days <= 90) return { variant: "warning", text: `${days}d left` };
  return { variant: "success", text: null };
};

const daysUntil = (date) => {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  return Math.ceil((new Date(date) - today) / 86400000);
};

export const ExpiryBadge = ({ date, label }) => {
  if (!date) return <span className="text-xs text-ink-300 font-mono">—</span>;
  const days = daysUntil(date);
  const fmt = new Date(date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  const tone = expiryTone(days);
  return (
    <div className="flex flex-col gap-1">
      {label && <span className="text-[11px] text-ink-400 font-medium">{label}</span>}
      <Badge variant={tone.variant} dot title={fmt}>{tone.text || fmt}</Badge>
    </div>
  );
};

export const ExpiryCell = ({ date, label }) => {
  if (!date) return <span className="text-xs text-ink-300">—</span>;
  const days = daysUntil(date);
  const fmt = new Date(date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  const tone = expiryTone(days);
  const color = { danger: "text-red-600", orange: "text-orange-600", warning: "text-amber-600", success: "text-emerald-600" }[tone.variant];
  return (
    <div className="text-xs leading-tight">
      <div className={cx("font-semibold flex items-center gap-1", color)}>
        <span className={cx("w-1.5 h-1.5 rounded-full", DOT_COLORS[tone.variant])} />
        {tone.text || fmt}
      </div>
      {label && <div className="text-ink-400 mt-0.5">{label}: {fmt}</div>}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
   Surfaces
   ═══════════════════════════════════════════════════════════════ */

export const Card = ({ children, className = "", hover = false, as: As = "div", ...rest }) => (
  <As
    className={cx(
      "bg-white border border-ink-200/80 rounded-2xl shadow-card",
      hover && "transition-all duration-200 hover:shadow-raised hover:border-ink-300/80",
      className
    )}
    {...rest}
  >
    {children}
  </As>
);

export const SectionCard = ({ title, subtitle, action, children, className = "", bodyClass = "", icon }) => (
  <div className={cx("bg-white border border-ink-200/80 rounded-2xl shadow-card overflow-hidden", className)}>
    {(title || action) && (
      <div className="flex items-center justify-between gap-3 px-4 sm:px-5 py-3.5 border-b border-ink-100">
        <div className="flex items-center gap-2.5 min-w-0">
          {icon && <span className="w-8 h-8 rounded-lg bg-ink-100 text-ink-500 grid place-items-center shrink-0">{icon}</span>}
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-ink-900 truncate">{title}</h3>
            {subtitle && <p className="text-xs text-ink-400 mt-0.5 truncate">{subtitle}</p>}
          </div>
        </div>
        {action && <div className="shrink-0">{action}</div>}
      </div>
    )}
    <div className={bodyClass}>{children}</div>
  </div>
);

/* ═══════════════════════════════════════════════════════════════
   Buttons
   ═══════════════════════════════════════════════════════════════ */

const BTN_VARIANTS = {
  primary:   "bg-brand-600 text-white shadow-xs hover:bg-brand-700 active:bg-brand-800",
  secondary: "bg-white text-ink-700 border border-ink-300 shadow-xs hover:bg-ink-50 hover:border-ink-400 active:bg-ink-100",
  outline:   "bg-white text-ink-700 border border-ink-300 hover:border-brand-400 hover:text-brand-700 hover:bg-brand-50 active:bg-brand-100",
  soft:      "bg-brand-50 text-brand-700 hover:bg-brand-100 active:bg-brand-200",
  ghost:     "text-ink-500 hover:text-ink-900 hover:bg-ink-100 active:bg-ink-200",
  danger:    "bg-red-600 text-white shadow-xs hover:bg-red-700 active:bg-red-800",
  dangerSoft:"bg-red-50 text-red-700 hover:bg-red-100 active:bg-red-200",
  success:   "bg-emerald-600 text-white shadow-xs hover:bg-emerald-700 active:bg-emerald-800",
  warning:   "bg-amber-500 text-white shadow-xs hover:bg-amber-600 active:bg-amber-700",
  dark:      "bg-ink-900 text-white shadow-xs hover:bg-ink-800 active:bg-ink-700",
};

const BTN_SIZES = {
  xs: "text-xs px-2.5 py-1.5 gap-1.5 rounded-lg",
  sm: "text-xs px-3 py-2 gap-1.5 rounded-lg",
  md: "text-sm px-4 py-2.5 gap-2 rounded-xl",
  lg: "text-sm px-5 py-3 gap-2 rounded-xl",
};

export const Button = ({
  children, onClick, variant = "primary", size = "md", disabled, loading,
  type = "button", className = "", to, href, icon, iconRight, block, title, ...rest
}) => {
  const cls = cx(
    "relative inline-flex items-center justify-center font-semibold whitespace-nowrap select-none",
    "transition-all duration-150 active:scale-[.98]",
    "disabled:opacity-50 disabled:pointer-events-none",
    RING,
    BTN_VARIANTS[variant] || BTN_VARIANTS.primary,
    BTN_SIZES[size] || BTN_SIZES.md,
    block && "w-full",
    className
  );
  const inner = (
    <>
      {loading && <LoaderCircle size={size === "xs" || size === "sm" ? 13 : 15} className="animate-spin shrink-0" />}
      {!loading && icon}
      {children}
      {!loading && iconRight}
    </>
  );
  if (to) return <Link to={to} className={cls} title={title} {...rest}>{inner}</Link>;
  if (href) return <a href={href} target="_blank" rel="noreferrer" className={cls} title={title} {...rest}>{inner}</a>;
  return (
    <button type={type} onClick={onClick} disabled={disabled || loading} className={cls} title={title} {...rest}>
      {inner}
    </button>
  );
};

export const IconButton = ({ icon, onClick, title, variant = "ghost", size = "md", className = "", disabled, to, ...rest }) => {
  const v = {
    ghost: "text-ink-400 hover:text-ink-800 hover:bg-ink-100 active:bg-ink-200",
    soft: "bg-ink-100 text-ink-600 hover:bg-ink-200",
    outline: "border border-ink-300 bg-white text-ink-600 hover:border-brand-400 hover:text-brand-600 hover:bg-brand-50",
    danger: "text-ink-400 hover:text-red-600 hover:bg-red-50 active:bg-red-100",
    brand: "text-brand-600 hover:bg-brand-50 active:bg-brand-100",
  };
  const s = { xs: "w-7 h-7", sm: "w-8 h-8", md: "w-10 h-10", lg: "w-11 h-11" };
  const cls = cx("inline-grid place-items-center rounded-lg transition-all active:scale-95 shrink-0 disabled:opacity-40 disabled:pointer-events-none",
    RING, v[variant] || v.ghost, s[size] || s.md, className);
  if (to) return <Link to={to} title={title} aria-label={title} className={cls} {...rest}>{icon}</Link>;
  return <button type="button" onClick={onClick} title={title} aria-label={title} disabled={disabled} className={cls} {...rest}>{icon}</button>;
};

/* ═══════════════════════════════════════════════════════════════
   Form fields
   ═══════════════════════════════════════════════════════════════ */

const fieldBase = (err, hasIcon, hasSuffix) => cx(
  "w-full bg-white text-ink-900 placeholder-ink-400 rounded-xl border text-sm",
  "px-3.5 py-2.5 transition-all duration-150 appearance-none",
  "focus:outline-none focus:ring-4",
  hasIcon && "pl-10",
  hasSuffix && "pr-10",
  err
    ? "border-red-400 focus:border-red-500 focus:ring-red-100"
    : "border-ink-300 hover:border-ink-400 focus:border-brand-500 focus:ring-brand-100",
  "disabled:bg-ink-50 disabled:text-ink-400 disabled:cursor-not-allowed"
);

const FieldShell = ({ label, hint, error, required, children, className = "" }) => (
  <div className={cx("flex flex-col gap-1.5", className)}>
    {label && (
      <label className="text-xs font-semibold text-ink-600 tracking-wide">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
    )}
    {children}
    {error
      ? <p className="text-xs text-red-600 flex items-center gap-1"><CircleAlert size={12} className="shrink-0" />{error}</p>
      : hint ? <p className="text-xs text-ink-400">{hint}</p> : null}
  </div>
);

export const Input = ({ label, error, hint, className = "", icon, suffix, required, wrapClass = "", ...props }) => (
  <FieldShell label={label} hint={hint} error={error} required={required} className={wrapClass}>
    <div className="relative">
      {icon && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400 pointer-events-none">{icon}</span>}
      <input className={cx(fieldBase(error, !!icon, !!suffix), className)} {...props} />
      {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-400 text-xs">{suffix}</span>}
    </div>
  </FieldShell>
);

export const Select = ({ label, error, hint, children, className = "", icon, required, wrapClass = "", ...props }) => (
  <FieldShell label={label} hint={hint} error={error} required={required} className={wrapClass}>
    <div className="relative">
      {icon && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400 pointer-events-none">{icon}</span>}
      <select className={cx(fieldBase(error, !!icon, true), "pr-9 cursor-pointer", className)} {...props}>{children}</select>
      <ChevronDown size={15} className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-400 pointer-events-none" />
    </div>
  </FieldShell>
);

export const Textarea = ({ label, error, hint, className = "", required, rows = 3, wrapClass = "", ...props }) => (
  <FieldShell label={label} hint={hint} error={error} required={required} className={wrapClass}>
    <textarea rows={rows} className={cx(fieldBase(error), "resize-y min-h-[76px]", className)} {...props} />
  </FieldShell>
);

export const Checkbox = ({ label, description, checked, onChange, disabled }) => (
  <label className={cx("flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-all",
    checked ? "bg-brand-50 border-brand-200" : "bg-white border-ink-200 hover:bg-ink-50",
    disabled && "opacity-50 pointer-events-none")}>
    <input type="checkbox" checked={checked} onChange={onChange} className="mt-0.5 w-4 h-4 rounded accent-brand-600 shrink-0" />
    <span className="min-w-0">
      <span className="block text-sm font-medium text-ink-800">{label}</span>
      {description && <span className="block text-xs text-ink-400 mt-0.5">{description}</span>}
    </span>
  </label>
);

export const Toggle = ({ checked, onChange, label, disabled }) => (
  <button type="button" role="switch" aria-checked={!!checked} disabled={disabled} onClick={() => onChange(!checked)}
    className={cx("inline-flex items-center gap-2.5 group", RING, disabled && "opacity-50 pointer-events-none")}>
    <span className={cx("relative w-9 h-5 rounded-full transition-colors shrink-0", checked ? "bg-brand-600" : "bg-ink-300")}>
      <span className={cx("absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow-sm transition-transform", checked && "translate-x-4")} />
    </span>
    {label && <span className="text-sm text-ink-700">{label}</span>}
  </button>
);

/* ═══════════════════════════════════════════════════════════════
   Search & filters
   ═══════════════════════════════════════════════════════════════ */

export const SearchBar = ({ value, onChange, placeholder = "Search…", className = "", autoFocus }) => (
  <div className={cx("relative", className)}>
    <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-ink-400 pointer-events-none" />
    <input
      type="search" value={value} autoFocus={autoFocus}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full bg-white border border-ink-300 rounded-xl pl-10 pr-9 py-2.5 text-sm text-ink-900 placeholder-ink-400
                 hover:border-ink-400 focus:outline-none focus:border-brand-500 focus:ring-4 focus:ring-brand-100 transition-all
                 [&::-webkit-search-cancel-button]:hidden"
    />
    {value && (
      <button type="button" onClick={() => onChange("")} aria-label="Clear search"
        className="absolute right-2.5 top-1/2 -translate-y-1/2 w-6 h-6 grid place-items-center rounded-full text-ink-400 hover:text-ink-700 hover:bg-ink-100 transition-all">
        <X size={13} />
      </button>
    )}
  </div>
);

export const FilterSelect = ({ value, onChange, children, className = "", ariaLabel }) => (
  <div className={cx("relative", className)}>
    <select value={value} onChange={e => onChange(e.target.value)} aria-label={ariaLabel}
      className={cx("w-full appearance-none bg-white border rounded-xl pl-3.5 pr-9 py-2.5 text-sm cursor-pointer transition-all",
        "hover:border-ink-400 focus:outline-none focus:border-brand-500 focus:ring-4 focus:ring-brand-100",
        value ? "border-brand-300 bg-brand-50/40 text-brand-800 font-medium" : "border-ink-300 text-ink-700")}>
      {children}
    </select>
    <ChevronDown size={15} className={cx("absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none", value ? "text-brand-500" : "text-ink-400")} />
  </div>
);

export const MonthPicker = ({ value, onChange, label = "Month", className = "" }) => (
  <input type="month" value={value} onChange={e => onChange(e.target.value)} title={label} aria-label={label}
    className={cx("bg-white border border-ink-300 rounded-xl px-3.5 py-2.5 text-sm text-ink-700 cursor-pointer",
      "hover:border-ink-400 focus:outline-none focus:border-brand-500 focus:ring-4 focus:ring-brand-100 transition-all", className)} />
);

// Horizontally scrollable on mobile so filters never wrap into a tall stack.
export const FilterBar = ({ children, className = "" }) => (
  <div className={cx("flex items-center gap-2.5 flex-wrap p-3 bg-white border border-ink-200/80 rounded-2xl shadow-card", className)}>
    {children}
  </div>
);

/* ═══════════════════════════════════════════════════════════════
   Pagination — compact on mobile, numbered on desktop
   ═══════════════════════════════════════════════════════════════ */

export const Pagination = ({ page, pages, total, limit, onPage }) => {
  if (!pages || pages <= 1) return null;
  const from = (page - 1) * limit + 1;
  const to = Math.min(page * limit, total);
  const nums = [];
  if (pages <= 7) { for (let i = 1; i <= pages; i++) nums.push(i); }
  else {
    nums.push(1);
    if (page > 3) nums.push("…");
    for (let i = Math.max(2, page - 1); i <= Math.min(pages - 1, page + 1); i++) nums.push(i);
    if (page < pages - 2) nums.push("…");
    nums.push(pages);
  }
  const navBtn = "h-9 min-w-9 px-2 grid place-items-center rounded-lg border border-ink-200 bg-white text-ink-600 text-sm font-medium transition-all hover:bg-brand-50 hover:border-brand-300 hover:text-brand-700 disabled:opacity-40 disabled:pointer-events-none";
  return (
    <div className="flex items-center justify-between gap-3 px-4 sm:px-5 py-3 border-t border-ink-100">
      <p className="text-xs text-ink-400 shrink-0">
        <span className="font-semibold text-ink-600 tnum">{from}–{to}</span>
        <span className="hidden xs:inline"> of </span><span className="xs:hidden">/</span>
        <span className="font-semibold text-ink-600 tnum">{total}</span>
      </p>
      <div className="flex items-center gap-1">
        <button onClick={() => onPage(page - 1)} disabled={page === 1} className={navBtn} aria-label="Previous page">
          <ChevronLeft size={16} />
        </button>
        <div className="hidden sm:flex items-center gap-1">
          {nums.map((n, i) => n === "…"
            ? <span key={`e${i}`} className="w-6 text-center text-ink-300 text-xs">…</span>
            : <button key={n} onClick={() => onPage(n)} aria-current={n === page ? "page" : undefined}
                className={cx("h-9 w-9 grid place-items-center rounded-lg text-sm font-medium transition-all tnum",
                  n === page ? "bg-brand-600 text-white shadow-xs" : "border border-ink-200 bg-white text-ink-600 hover:bg-brand-50 hover:border-brand-300 hover:text-brand-700")}>
                {n}
              </button>
          )}
        </div>
        <span className="sm:hidden text-xs text-ink-500 px-2 tnum font-medium">{page} / {pages}</span>
        <button onClick={() => onPage(page + 1)} disabled={page === pages} className={navBtn} aria-label="Next page">
          <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
   Full-height list layout

   Listing screens fill the viewport instead of growing the page:
   the header and filters stay put, only the rows scroll, and the
   pager is pinned to the bottom. Use inside <AdminLayout fullHeight>.
   ═══════════════════════════════════════════════════════════════ */

// Vertical stack that consumes exactly the height AdminLayout gives it
export const ListPage = ({ children, className = "" }) => (
  <div className={cx("flex flex-col h-full min-h-0 gap-4", className)}>{children}</div>
);

/* The breakpoint a listing screen switches from cards to a table.
   "lg" suits most tables now that the rail collapses to icons on tablet (~900px
   of content at 1024px). "xl" is for the few too dense to fit that — pass the
   same value to ListSurface, ListSkeleton and the table/card class pair. */
const SURFACE = {
  lg: {
    chrome: "lg:bg-white lg:border lg:border-ink-200/80 lg:shadow-card",
    footer: "lg:border-0 lg:border-t lg:border-ink-100 lg:rounded-none lg:rounded-b-2xl lg:mt-0",
  },
  xl: {
    chrome: "xl:bg-white xl:border xl:border-ink-200/80 xl:shadow-card",
    footer: "xl:border-0 xl:border-t xl:border-ink-100 xl:rounded-none xl:rounded-b-2xl xl:mt-0",
  },
};

// The scrolling region. Card chrome wraps the table from `from` up; below that
// the rows are already individual cards, so the surface stays a bare scroller.
export const ListSurface = ({ children, footer, from = "lg", className = "" }) => {
  const s = SURFACE[from] || SURFACE.lg;
  return (
    <div className={cx("flex-1 min-h-0 flex flex-col overflow-hidden rounded-2xl", s.chrome, className)}>
      <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain">{children}</div>
      {footer && (
        <div className={cx("shrink-0 bg-white border border-ink-200/80 rounded-2xl mt-3", s.footer)}>
          {footer}
        </div>
      )}
    </div>
  );
};

// Header row that stays visible while the body scrolls under it
export const stickyHeadCls = "sticky top-0 z-20 bg-ink-50 border-b border-ink-200/70";

// First cell of every row: pinned left so the actions never scroll out of reach
export const actionColCls = "sticky left-0 z-10 bg-white group-hover:bg-ink-50 transition-colors";
export const actionHeadCls = "sticky left-0 z-30 bg-ink-50";

/* ── Listing tables ──────────────────────────────────────────────
   A fixed layout is what keeps rows inside the page: every column gets
   the width the header declares, and long values truncate instead of
   pushing the table sideways. The table only appears at xl, where the
   content column is genuinely wide enough; below that the same records
   render as cards, which have no width to run out of.

   Pair `listTableCls` with `listCardsCls`, and give every text cell a
   `truncate` (or `line-clamp-*`) child so nothing spills.

   Most tables appear at lg — with the rail collapsed to icons on tablet there
   is ~900px to work with there. The `Wide` pair holds a table back to xl for
   the rare screen whose columns genuinely can't be read at that width. */
export const listTableCls = "hidden lg:table w-full table-fixed text-sm";
export const listCardsCls = "lg:hidden grid grid-cols-1 sm:grid-cols-2 gap-3 pb-3";

export const listTableWideCls = "hidden xl:table w-full table-fixed text-sm";
export const listCardsWideCls = "xl:hidden grid grid-cols-1 sm:grid-cols-2 gap-3 pb-3";

// Header / body cells sized for a fixed-layout table
export const thCls = "text-left px-4 py-3 text-[11px] font-semibold text-ink-500 uppercase tracking-wider truncate";
export const tdCls = "px-4 py-3.5 align-middle";

// Width of the leading actions column, sized to the icon buttons it holds
// (32px each + 2px gaps + 24px cell padding). Classes are spelled out so
// Tailwind's scanner keeps them.
export const actionW = (buttons = 2) => ({
  2: "w-[104px]", 3: "w-[136px]", 4: "w-[168px]", 5: "w-[200px]",
}[buttons] || "w-[104px]");

/* ═══════════════════════════════════════════════════════════════
   Modal — centred dialog on desktop, bottom sheet on phones
   ═══════════════════════════════════════════════════════════════ */

export const Modal = ({ open, onClose, title, subtitle, children, size = "md", footer, icon }) => {
  const panelRef = useRef(null);

  // Callers pass an inline arrow, so onClose is a new function every render.
  // Keeping it in a ref keeps it out of the effect deps below — see the comment
  // on that effect for why re-running it on every keystroke is destructive.
  const closeRef = useRef(onClose);
  useEffect(() => { closeRef.current = onClose; });

  /* Deps are [open] alone and deliberately so. With onClose in the list the
     effect re-ran on every keystroke (new arrow identity → deps changed), which
     re-armed the focus timer below and pulled focus off the field the user was
     typing in — on touch devices that dismisses the on-screen keyboard. The
     re-run also re-captured `prev` as "hidden", so closing the modal left the
     page permanently scroll-locked. */
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") closeRef.current?.(); };
    document.addEventListener("keydown", onKey);
    // move focus into the dialog so Esc/Tab behave — but never steal it from a
    // field that already has it (autoFocus, or a tap that beat the timer)
    const t = setTimeout(() => {
      const panel = panelRef.current;
      if (panel && !panel.contains(document.activeElement)) panel.focus();
    }, 30);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", onKey);
      clearTimeout(t);
    };
  }, [open]);

  if (!open) return null;
  const s = { xs: "sm:max-w-sm", sm: "sm:max-w-md", md: "sm:max-w-lg", lg: "sm:max-w-2xl", xl: "sm:max-w-4xl", full: "sm:max-w-6xl" };

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center sm:p-4" role="dialog" aria-modal="true" aria-label={title}>
      <div className="absolute inset-0 bg-ink-900/40 backdrop-blur-[2px] animate-fade-in" onClick={onClose} />
      <div
        ref={panelRef} tabIndex={-1}
        className={cx(
          "relative w-full bg-white shadow-pop flex flex-col outline-none",
          "rounded-t-3xl sm:rounded-2xl",
          "max-h-[92dvh] sm:max-h-[88vh]",
          "animate-sheet-up sm:animate-scale-in",
          s[size] || s.md
        )}
      >
        {/* grab handle — signals "drag/close" affordance on touch */}
        <div className="sm:hidden pt-3 pb-1 grid place-items-center shrink-0">
          <span className="w-10 h-1.5 rounded-full bg-ink-200" />
        </div>

        <div className="flex items-start justify-between gap-3 px-5 sm:px-6 py-3.5 sm:py-4 border-b border-ink-100 shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            {icon && <span className="w-9 h-9 rounded-xl bg-brand-50 text-brand-600 grid place-items-center shrink-0">{icon}</span>}
            <div className="min-w-0">
              <h2 className="text-base font-bold text-ink-900 truncate">{title}</h2>
              {subtitle && <p className="text-xs text-ink-400 mt-0.5 truncate">{subtitle}</p>}
            </div>
          </div>
          <IconButton size="sm" icon={<X size={17} />} onClick={onClose} title="Close" />
        </div>

        <div className="overflow-y-auto overscroll-contain flex-1 px-5 sm:px-6 py-5">{children}</div>

        {footer && (
          <div className="border-t border-ink-100 px-5 sm:px-6 py-3.5 bg-ink-50/70 rounded-b-2xl shrink-0 safe-bottom">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

export const ConfirmModal = ({ open, onClose, onConfirm, title, message, confirmLabel = "Delete", danger = true, loading }) => (
  <Modal open={open} onClose={onClose} title={title} size="sm"
    icon={danger ? <TriangleAlert size={18} /> : <Info size={18} />}>
    <p className="text-ink-600 text-sm leading-relaxed">{message}</p>
    <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2.5 mt-6">
      <Button variant="secondary" onClick={onClose} block className="sm:w-auto">Cancel</Button>
      <Button variant={danger ? "danger" : "primary"} onClick={onConfirm} loading={loading} block className="sm:w-auto">{confirmLabel}</Button>
    </div>
  </Modal>
);

// Slide-over panel — used for filters and long forms on small screens
export const Drawer = ({ open, onClose, title, children, footer, side = "right" }) => {
  const closeRef = useRef(onClose);
  useEffect(() => { closeRef.current = onClose; });

  // [open] alone — same reasoning as Modal: an inline onClose re-ran this on
  // every keystroke and re-captured `prev` as "hidden", leaking the scroll lock
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => e.key === "Escape" && closeRef.current?.();
    document.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = prev; document.removeEventListener("keydown", onKey); };
  }, [open]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60]" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-ink-900/40 backdrop-blur-[2px] animate-fade-in" onClick={onClose} />
      <div className={cx("absolute top-0 bottom-0 w-full max-w-sm bg-white shadow-pop flex flex-col animate-fade-in",
        side === "right" ? "right-0" : "left-0")}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-ink-100 shrink-0">
          <h2 className="text-base font-bold text-ink-900">{title}</h2>
          <IconButton size="sm" icon={<X size={17} />} onClick={onClose} title="Close" />
        </div>
        <div className="flex-1 overflow-y-auto overscroll-contain px-5 py-4">{children}</div>
        {footer && <div className="border-t border-ink-100 px-5 py-3.5 bg-ink-50/70 shrink-0 safe-bottom">{footer}</div>}
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
   Empty / loading states
   ═══════════════════════════════════════════════════════════════ */

export const EmptyState = ({ icon, title, description, action, compact = false }) => (
  <div className={cx("flex flex-col items-center justify-center text-center px-6", compact ? "py-10" : "py-16")}>
    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-ink-100 to-ink-50 border border-ink-200/60 grid place-items-center text-2xl text-ink-400 mb-4">
      {icon || <Inbox size={22} />}
    </div>
    <h3 className="text-base font-semibold text-ink-800">{title}</h3>
    {description && <p className="text-ink-400 text-sm mt-1 max-w-sm leading-relaxed">{description}</p>}
    {action && <div className="mt-5">{action}</div>}
  </div>
);

export const Spinner = ({ size = "md", className = "" }) => {
  const s = { xs: 14, sm: 16, md: 22, lg: 34 };
  return <LoaderCircle size={s[size] || s.md} className={cx("animate-spin text-brand-600", className)} />;
};

export const PageLoader = ({ label = "Loading…" }) => (
  <div className="flex flex-col items-center justify-center py-24 gap-3">
    <Spinner size="lg" />
    <p className="text-sm text-ink-400">{label}</p>
  </div>
);

export const Skeleton = ({ className = "" }) => <div className={cx("shimmer rounded-lg", className)} />;

export const TableSkeleton = ({ rows = 5, cols = 5 }) => (
  <div>
    {Array.from({ length: rows }).map((_, r) => (
      <div key={r} className="flex items-center gap-4 px-5 py-4 border-b border-ink-50 last:border-0">
        {Array.from({ length: cols }).map((_, c) => (
          <Skeleton key={c} className={cx("h-3.5", c === 0 ? "w-40" : c === 1 ? "w-24" : "w-16")} />
        ))}
      </div>
    ))}
  </div>
);

export const CardSkeleton = ({ lines = 3 }) => (
  <Card className="p-4 space-y-3">
    <div className="flex items-center gap-3">
      <Skeleton className="w-11 h-11 rounded-xl shrink-0" />
      <div className="flex-1 space-y-2">
        <Skeleton className="h-3.5 w-2/3" />
        <Skeleton className="h-2.5 w-1/3" />
      </div>
    </div>
    {Array.from({ length: lines - 1 }).map((_, i) => <Skeleton key={i} className="h-3 w-full" />)}
  </Card>
);

// Renders a table skeleton on desktop and card skeletons on mobile in one shot.
// Mirrors the table/card split of a listing screen so the shape doesn't jump on
// load. `from` must match the ListSurface it stands in for.
export const ListSkeleton = ({ rows = 6, cols = 6, from = "lg" }) => (
  <>
    <Card className={cx("hidden overflow-hidden", from === "xl" ? "xl:block" : "lg:block")}>
      <TableSkeleton rows={rows} cols={cols} />
    </Card>
    <div className={cx("grid grid-cols-1 sm:grid-cols-2 gap-3", from === "xl" ? "xl:hidden" : "lg:hidden")}>
      {Array.from({ length: Math.min(rows, 4) }).map((_, i) => <CardSkeleton key={i} />)}
    </div>
  </>
);

/* ═══════════════════════════════════════════════════════════════
   Stats
   ═══════════════════════════════════════════════════════════════ */

const STAT_TONES = {
  blue:    { chip: "bg-brand-50 text-brand-600", bar: "from-brand-500 to-brand-400" },
  brand:   { chip: "bg-brand-50 text-brand-600", bar: "from-brand-500 to-brand-400" },
  emerald: { chip: "bg-emerald-50 text-emerald-600", bar: "from-emerald-500 to-emerald-400" },
  amber:   { chip: "bg-amber-50 text-amber-600", bar: "from-amber-500 to-amber-400" },
  purple:  { chip: "bg-violet-50 text-violet-600", bar: "from-violet-500 to-violet-400" },
  red:     { chip: "bg-red-50 text-red-600", bar: "from-red-500 to-red-400" },
  orange:  { chip: "bg-orange-50 text-orange-600", bar: "from-orange-500 to-orange-400" },
  cyan:    { chip: "bg-cyan-50 text-cyan-600", bar: "from-cyan-500 to-cyan-400" },
  slate:   { chip: "bg-ink-100 text-ink-600", bar: "from-ink-400 to-ink-300" },
};

export const StatCard = ({ label, value, icon, color = "blue", sub, trend, to, loading }) => {
  const tone = STAT_TONES[color] || STAT_TONES.blue;
  const inner = (
    <div className={cx(
      "relative h-full bg-white border border-ink-200/80 rounded-2xl p-4 shadow-card overflow-hidden",
      "transition-all duration-200",
      to && "hover:shadow-raised hover:border-ink-300 hover:-translate-y-0.5"
    )}>
      <span className={cx("absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r", tone.bar)} />
      <div className="flex items-start justify-between gap-2">
        <span className={cx("w-9 h-9 rounded-xl grid place-items-center text-base shrink-0", tone.chip)}>{icon}</span>
        {trend !== undefined && trend !== null && (
          <Badge variant={trend >= 0 ? "success" : "danger"} size="xs">
            {trend >= 0 ? "↑" : "↓"} {Math.abs(trend)}%
          </Badge>
        )}
      </div>
      <div className="mt-3">
        {loading
          ? <Skeleton className="h-7 w-20" />
          : <div className="text-2xl font-bold text-ink-900 font-display leading-none tnum truncate">{value}</div>}
        <div className="text-[13px] text-ink-500 mt-1.5 font-medium truncate">{label}</div>
        {sub && <div className="text-xs text-ink-400 mt-0.5 truncate">{sub}</div>}
      </div>
    </div>
  );
  return to ? <Link to={to} className="block h-full">{inner}</Link> : inner;
};

export const ProgressBar = ({ value = 0, max = 100, tone = "brand", label, className = "" }) => {
  const pct = max > 0 ? Math.min(100, Math.max(0, (value / max) * 100)) : 0;
  const colors = { brand: "bg-brand-500", emerald: "bg-emerald-500", amber: "bg-amber-500", red: "bg-red-500" };
  return (
    <div className={className}>
      {label && (
        <div className="flex justify-between text-xs mb-1.5">
          <span className="text-ink-500">{label}</span>
          <span className="font-semibold text-ink-700 tnum">{Math.round(pct)}%</span>
        </div>
      )}
      <div className="h-1.5 rounded-full bg-ink-100 overflow-hidden">
        <div className={cx("h-full rounded-full transition-all duration-500", colors[tone] || colors.brand)} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

export const Avatar = ({ name, src, size = "md", tone = "brand", className = "" }) => {
  const s = { xs: "w-6 h-6 text-[10px]", sm: "w-8 h-8 text-xs", md: "w-10 h-10 text-sm", lg: "w-12 h-12 text-base", xl: "w-16 h-16 text-xl" };
  const tones = {
    brand: "bg-brand-100 text-brand-700", emerald: "bg-emerald-100 text-emerald-700",
    violet: "bg-violet-100 text-violet-700", amber: "bg-amber-100 text-amber-700", slate: "bg-ink-200 text-ink-600",
  };
  const initials = (name || "?").trim().split(/\s+/).slice(0, 2).map(w => w[0]).join("").toUpperCase();
  if (src) return <img src={src} alt={name} className={cx("rounded-full object-cover shrink-0", s[size], className)} />;
  return (
    <span className={cx("rounded-full grid place-items-center font-bold shrink-0 select-none", s[size] || s.md, tones[tone] || tones.brand, className)}>
      {initials}
    </span>
  );
};

/* ═══════════════════════════════════════════════════════════════
   Page chrome
   ═══════════════════════════════════════════════════════════════ */

export const PageHeader = ({ title, subtitle, action, back, children, badge }) => (
  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
    <div className="flex items-start gap-3 min-w-0">
      {back}
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <h1 className="text-xl sm:text-2xl font-bold text-ink-900 font-display leading-tight truncate">{title}</h1>
          {badge}
        </div>
        {subtitle && <p className="text-ink-400 text-sm mt-1">{subtitle}</p>}
      </div>
    </div>
    {(children || action) && (
      <div className="flex items-center gap-2 flex-wrap shrink-0">
        {children}
        {action}
      </div>
    )}
  </div>
);

export const BackButton = ({ to, onClick, label = "Back" }) => (
  <IconButton to={to} onClick={onClick} title={label} variant="outline" size="md"
    icon={<ChevronLeft size={18} />} className="mt-0.5" />
);

// Scrollable on mobile so many tabs never wrap
export const Tabs = ({ tabs, active, onChange, variant = "pill" }) => {
  if (variant === "underline") {
    return (
      <div className="flex gap-1 overflow-x-auto no-scrollbar border-b border-ink-200 -mb-px">
        {tabs.map(t => (
          <button key={t.key} onClick={() => onChange(t.key)}
            className={cx("px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-all shrink-0",
              active === t.key ? "border-brand-600 text-brand-700" : "border-transparent text-ink-500 hover:text-ink-800 hover:border-ink-300")}>
            {t.label}
            {t.count !== undefined && <span className="ml-1.5 text-xs text-ink-400 tnum">{t.count}</span>}
          </button>
        ))}
      </div>
    );
  }
  return (
    <div className="flex gap-1 p-1 bg-ink-100 rounded-xl overflow-x-auto no-scrollbar">
      {tabs.map(t => (
        <button key={t.key} onClick={() => onChange(t.key)}
          className={cx("px-3.5 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap shrink-0 flex items-center gap-1.5",
            active === t.key ? "bg-white text-ink-900 shadow-xs" : "text-ink-500 hover:text-ink-800")}>
          {t.icon}
          {t.label}
          {t.count !== undefined && (
            <span className={cx("text-xs px-1.5 rounded-full tnum", active === t.key ? "bg-brand-100 text-brand-700" : "bg-ink-200 text-ink-500")}>{t.count}</span>
          )}
        </button>
      ))}
    </div>
  );
};

export const Divider = ({ label, className = "" }) => (
  <div className={cx("flex items-center gap-3", className)}>
    <div className="flex-1 h-px bg-ink-200" />
    {label && <span className="text-xs text-ink-400 font-semibold uppercase tracking-wider">{label}</span>}
    {label && <div className="flex-1 h-px bg-ink-200" />}
  </div>
);

export const AlertBanner = ({ type = "warning", title, message, onDismiss, action }) => {
  const t = {
    warning: { box: "bg-amber-50 border-amber-200 text-amber-900", icon: <TriangleAlert size={17} className="text-amber-600" /> },
    danger:  { box: "bg-red-50 border-red-200 text-red-900", icon: <CircleAlert size={17} className="text-red-600" /> },
    info:    { box: "bg-brand-50 border-brand-200 text-brand-900", icon: <Info size={17} className="text-brand-600" /> },
    success: { box: "bg-emerald-50 border-emerald-200 text-emerald-900", icon: <CircleCheck size={17} className="text-emerald-600" /> },
  };
  const cfg = t[type] || t.warning;
  return (
    <div className={cx("border rounded-2xl px-4 py-3.5 flex items-start gap-3", cfg.box)}>
      <span className="mt-0.5 shrink-0">{cfg.icon}</span>
      <div className="flex-1 min-w-0">
        {title && <p className="font-semibold text-sm">{title}</p>}
        {message && <p className="text-sm mt-0.5 opacity-90 leading-relaxed">{message}</p>}
        {action && <div className="mt-2.5">{action}</div>}
      </div>
      {onDismiss && <IconButton size="xs" icon={<X size={14} />} onClick={onDismiss} title="Dismiss" className="-mr-1 -mt-1" />}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
   Tables
   ═══════════════════════════════════════════════════════════════ */

// Scroll container that keeps wide tables inside the card instead of
// stretching the page. Pair with `hidden md:block` + mobile cards.
export const TableWrap = ({ children, className = "" }) => (
  <div className={cx("overflow-x-auto overscroll-x-contain", className)}>{children}</div>
);

export const Th = ({ children, className = "", align = "left" }) => (
  <th className={cx("px-5 py-3 text-[11px] font-semibold text-ink-500 uppercase tracking-wider whitespace-nowrap",
    align === "right" ? "text-right" : align === "center" ? "text-center" : "text-left", className)}>
    {children}
  </th>
);

// Clickable header that reflects and toggles sort state
export const SortTh = ({ label, field, sort, onSort, align = "left", className = "" }) => {
  if (!field) return <Th align={align} className={className}>{label}</Th>;
  const active = sort?.by === field;
  const Arrow = !active ? ArrowUpDown : sort.order === "asc" ? ArrowUp : ArrowDown;
  return (
    <th className={cx("px-5 py-3 text-[11px] font-semibold uppercase tracking-wider whitespace-nowrap",
      align === "right" ? "text-right" : "text-left", active ? "text-brand-700" : "text-ink-500", className)}>
      <button type="button" onClick={() => onSort(field)}
        className={cx("inline-flex items-center gap-1.5 hover:text-brand-700 transition-colors group", RING, "rounded")}>
        {label}
        <Arrow size={12} className={cx("transition-opacity", active ? "opacity-100" : "opacity-30 group-hover:opacity-70")} />
      </button>
    </th>
  );
};

export const Td = ({ children, className = "", align = "left" }) => (
  <td className={cx("px-5 py-3.5 align-middle",
    align === "right" ? "text-right" : align === "center" ? "text-center" : "text-left", className)}>
    {children}
  </td>
);

export const Table = ({ headers, children, loading, empty, className = "" }) => (
  <TableWrap>
    <table className={cx("w-full text-sm", className)}>
      <thead className="bg-ink-50/80 border-b border-ink-200/70">
        <tr>{headers.map((h, i) => <Th key={i}>{h}</Th>)}</tr>
      </thead>
      <tbody className="divide-y divide-ink-100">
        {loading ? <tr><td colSpan={headers.length}><TableSkeleton rows={5} cols={headers.length} /></td></tr> : children}
      </tbody>
    </table>
    {empty}
  </TableWrap>
);

export const Tr = ({ children, className = "", onClick }) => (
  <tr onClick={onClick} className={cx("transition-colors hover:bg-ink-50/70", onClick && "cursor-pointer", className)}>
    {children}
  </tr>
);

/* ═══════════════════════════════════════════════════════════════
   Small helpers used across pages
   ═══════════════════════════════════════════════════════════════ */

// label / value pair for detail panels
export const Field = ({ label, value, className = "" }) => (
  <div className={cx("min-w-0", className)}>
    <dt className="text-[11px] font-semibold text-ink-400 uppercase tracking-wider">{label}</dt>
    <dd className="text-sm text-ink-800 mt-1 break-words">{value || <span className="text-ink-300">—</span>}</dd>
  </div>
);

export const Chip = ({ children, tone = "slate", onRemove, className = "", icon }) => {
  const tones = {
    slate: "bg-ink-100 text-ink-700", brand: "bg-brand-50 text-brand-700",
    violet: "bg-violet-50 text-violet-700", sky: "bg-sky-50 text-sky-700",
    amber: "bg-amber-50 text-amber-700", emerald: "bg-emerald-50 text-emerald-700",
  };
  return (
    <span className={cx("inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium max-w-full", tones[tone] || tones.slate, className)}>
      {icon}
      <span className="truncate">{children}</span>
      {onRemove && <button type="button" onClick={onRemove} className="hover:opacity-70 shrink-0"><X size={11} /></button>}
    </span>
  );
};

// Sticky action bar for mobile forms
export const StickyActions = ({ children, className = "" }) => (
  <div className={cx("sticky bottom-0 -mx-5 sm:-mx-6 -mb-5 px-5 sm:px-6 py-3.5 bg-white/95 backdrop-blur border-t border-ink-100 flex flex-col-reverse sm:flex-row sm:justify-end gap-2.5 safe-bottom", className)}>
    {children}
  </div>
);

/* ═══════════════════════════════════════════════════════════════
   Hooks
   ═══════════════════════════════════════════════════════════════ */

export const useMediaQuery = (query) => {
  const [matches, setMatches] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia(query).matches : false);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const handler = (e) => setMatches(e.matches);
    setMatches(mq.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [query]);
  return matches;
};

export const useIsMobile = () => useMediaQuery("(max-width: 767px)");

// Debounced value — for search boxes
export const useDebounced = (value, delay = 300) => {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
};

/* Formatting helpers shared by every list/detail page */
export const formatINR = (amount, { compact = false } = {}) => {
  const n = Number(amount || 0);
  if (compact && Math.abs(n) >= 1e7) return `₹${(n / 1e7).toFixed(2)}Cr`;
  if (compact && Math.abs(n) >= 1e5) return `₹${(n / 1e5).toFixed(2)}L`;
  return `₹${n.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
};

export const formatDate = (d, opts = { day: "numeric", month: "short", year: "numeric" }) =>
  d ? new Date(d).toLocaleDateString("en-IN", opts) : "—";

/* ═══════════════════════════════════════════════════════════════
   Dates — relative phrasing and project timelines
   ═══════════════════════════════════════════════════════════════ */

const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };

// Whole days from today. Negative = past, 0 = today, positive = future.
export const daysFromToday = (d) => {
  if (!d) return null;
  const then = startOfDay(d);
  if (Number.isNaN(then.getTime())) return null;
  return Math.round((then - startOfDay(new Date())) / 86400000);
};

/** "today" · "yesterday" · "5 days ago" · "in 3 weeks" · "8 Mar 2026" */
export const relativeDate = (d) => {
  const days = daysFromToday(d);
  if (days === null) return "—";
  const n = Math.abs(days);
  if (n === 0) return "today";
  if (n === 1) return days < 0 ? "yesterday" : "tomorrow";
  if (n < 7) return days < 0 ? `${n} days ago` : `in ${n} days`;
  if (n < 31) {
    const w = Math.round(n / 7);
    return days < 0 ? `${w} week${w > 1 ? "s" : ""} ago` : `in ${w} week${w > 1 ? "s" : ""}`;
  }
  if (n < 365) {
    const m = Math.round(n / 30);
    return days < 0 ? `${m} month${m > 1 ? "s" : ""} ago` : `in ${m} month${m > 1 ? "s" : ""}`;
  }
  return formatDate(d);
};

/** Absolute date with the relative phrasing after it — "8 Mar 2026 · in 3 days" */
export const dateWithRelative = (d) => (d ? `${formatDate(d)} · ${relativeDate(d)}` : "—");

/**
 * Where a site sits in its own schedule, from its start/end dates and status.
 * Returns a label, a tone for the badge, and the number that matters.
 */
export const siteTimeline = (site = {}) => {
  const { startDate, endDate, status } = site;
  const toStart = daysFromToday(startDate);
  const toEnd = daysFromToday(endDate);

  if (status === "completed") return { label: "Completed", tone: "info", detail: endDate ? relativeDate(endDate) : null };
  if (status === "on-hold") return { label: "On hold", tone: "warning", detail: null };
  if (status === "inactive") return { label: "Inactive", tone: "default", detail: null };

  if (!startDate && !endDate) return { label: "No dates set", tone: "default", detail: null };

  if (toStart !== null && toStart > 0) return { label: `Starts ${relativeDate(startDate)}`, tone: "purple", detail: null };

  if (toEnd !== null) {
    if (toEnd < 0) return { label: `Overdue by ${Math.abs(toEnd)}d`, tone: "danger", detail: null };
    if (toEnd === 0) return { label: "Ends today", tone: "warning", detail: null };
    if (toEnd <= 14) return { label: `Ends in ${toEnd}d`, tone: "warning", detail: null };
    return { label: `Ends ${relativeDate(endDate)}`, tone: "success", detail: null };
  }

  // Running with no end date — say how long it has been going
  const running = toStart !== null ? Math.abs(toStart) : null;
  return { label: running !== null ? `Running ${running}d` : "Running", tone: "success", detail: null };
};

/** How stale a site is — used to colour the "last activity" line */
export const activityTone = (d) => {
  const days = daysFromToday(d);
  if (days === null) return "default";
  const n = Math.abs(days);
  if (n <= 7) return "success";
  if (n <= 30) return "warning";
  return "danger";
};
